<script setup>
  import  {ref, computed, onUnmounted} from 'vue';
  import { SpectralPainter } from "@/components/cry-creator/spectral-painter.js";

  import { ArrowBigDownIcon, ArrowBigUpIcon, AudioLinesIcon, BrushIcon, CheckIcon, DicesIcon, DownloadIcon, EraserIcon, EyeClosedIcon, EyeIcon, ImageIcon, MonitorDownIcon, MonitorUpIcon, PlayIcon, PlusIcon, RedoIcon, ShareIcon, SquareDashedIcon, SquareIcon, UndoIcon, XIcon } from 'lucide-vue-next';

  import { onMounted } from "vue";

  import BaseCard from "../components/BaseCard.vue";
  import ToolPageHeader from "../components/ToolPageHeader.vue";
import InputGroup from '@/components/InputGroup.vue';
import Button from '@/components/Button.vue';
import Tooltip from '@/components/Tooltip.vue';

import PokeballIcon from '@/components/PokeballIcon.vue'
  import PokemonSearch from "../components/PokemonSearch.vue";
import HoverDropdown from '@/components/HoverDropdown.vue';
import Modal from '@/components/Modal.vue';



  const painter = ref(null)
  const mainCanvas = ref();
  const selectionOverlay = ref();
  const playhead = ref()

  const pngFile = ref()
  const wavFile = ref()
  const cryFile = ref();

  //PITA but here we are
  // Playback
  const _playbackIters = ref(5);

  // Export
  const _glItersExport = ref(60);

  // Other
  const _name = ref('')

  //Painting
  const _tool = ref('brush')
  const _brushOpacity = ref(100);
  const _brushSize = ref(10);
  const _brushSoftness = ref(2);
  const _noiseScale = ref(20);
  const _brushType = ref('paint')
  const _eraseMode = ref(false)
  const _undoSteps = ref(20)

  const layers = ref([])
  const selectedLayer = ref(null)
  const isBusy = ref(false)
  const isPlaying = ref(false)
  const statusText = ref('Ready.')
  const progress = ref(0)


  const keydown = (e) => {
    // Ctrl/Cmd shortcuts work regardless of focus
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'z') { e.preventDefault(); e.shiftKey ? painter.value.redo() : painter.value.undo(); }
      if (e.key === 'y') { e.preventDefault(); painter.value.redo(); }
      if (e.key === 'a') { e.preventDefault(); painter.value.selectAll(); }
      return;
    }

    // Plain-key shortcuts: skip when a form control has focus
    if (e.target !== document.body && e.target !== document.documentElement) return;
    if (e.altKey) return;

    if (e.key === 'b' || e.key === 'B') {
      if (tool.value === 'brush') {
        painter.value.toggleNoiseBrush();
      } else {
        painter.value.setTool('brush');
      }
    }

    if (e.key === 'm' || e.key === 'M') {
      painter.value.setTool('select');
    }

    if (e.key === ' ') {
      e.preventDefault();
      if (painter.value.playSource) {
        painter.value.stop();
      } else {
        painter.value.play();
      }
    }

    // 1–9 = 10%–90%, 0 = 100%
    if (e.key >= '0' && e.key <= '9') {
      const pct = e.key === '0' ? 100 : parseInt(e.key) * 10;
      painter.value.setBrushOpacity(pct / 100);
    }

    // [ / ] decrease / increase brush size by 1, clamped to slider range
    if (e.key === '[' || e.key === ']') {
      e.preventDefault();
      const next = Math.max(1, Math.min(60, painter.value.brushSize + (e.key === ']' ? 1 : -1)));
      painter.value.setBrushSize(next);
      // brushSizeEl.value = next;
    }
  }

  onMounted(()=>{
    painter.value = new SpectralPainter();
    painter.value.fileExtension = 'cry'

    // Set refs
    _playbackIters.value = painter.value.glItersPlay
    _glItersExport.value = painter.value.glItersExport
    _tool.value = painter.value._tool
    _brushOpacity.value = painter.value.brushOpacity * 100
    _brushSize.value = painter.value.brushSize
    _brushSoftness.value = painter.value.brushSoftness
    _noiseScale.value = painter.value.noiseScale
    _brushType.value = painter.value.brushType
    _eraseMode.value = painter.value.eraseMode
    _undoSteps.value = painter.value._undoLimit


    document.addEventListener('keydown', keydown);

    // Playback
    painter.value.addEventListener('glitersplaychange',
      (e) => {
        _playbackIters.value = Math.round(e.detail.value)
    });

    // Export
    painter.value.addEventListener('glitersexportchange',
      (e) => {
        _glItersExport.value = Math.round(e.detail.value)
    });

    // Other
    painter.value.addEventListener('namechange',
      (e) => {
        _name.value = e.detail.name
    });


    //Painting
    painter.value.addEventListener('toolchange',
      (e) => {
        _tool.value = e.detail.tool
    });
    painter.value.addEventListener('brushtypechange',
      (e) => {
        _brushType.value = e.detail.type
    });
    painter.value.addEventListener('erasechange',
      (e) => {
        _eraseMode.value = e.detail.active
    });
    painter.value.addEventListener('brushopacitychange',
      (e) => {
        _brushOpacity.value = Math.round(e.detail.value * 100)
    });
    painter.value.addEventListener('brushsizechange',
      (e) => {
        _brushSize.value = e.detail.value
    });
    painter.value.addEventListener('softnesschange',
      (e) => {
        _brushSoftness.value = e.detail.value
    });
    painter.value.addEventListener('noisescalechange',
      (e) => {
        _noiseScale.value = e.detail.value
    });

    painter.value.addEventListener('undolimitchange',
      (e) => {
        _undoSteps.value = e.detail.value
    });


    painter.value.addEventListener('layerschange',
      (e) => {
        layers.value = e.detail.layers
        selectedLayer.value = e.detail.selectedId
    });

    painter.value.addEventListener('busychange', (e) => {
      isBusy.value = e.detail.busy
    });
    painter.value.addEventListener('playstatechange', (e) => {
      isPlaying.value = e.detail.playing;
      playhead.value.style.display = e.detail.playing ? 'block' : 'none';
    });

    painter.value.addEventListener('playheadmove', (e) => {
      playhead.value.style.left = (e.detail.progress * 512) + 'px';
    });

    painter.value.addEventListener('statuschange',   (e) => {
      statusText.value = e.detail.message
    });
    painter.value.addEventListener('progresschange', (e) => {
      progress.value = e.detail.percent
    });





    painter.value.setup(mainCanvas.value)
    painter.value.setupOverlay(selectionOverlay.value);
    drawAxes();



  })

  onUnmounted(()=>{
    document.removeEventListener('keydown', keydown)
  })


  const yAxisPoints = ref([])
  const drawAxes = () => {
    let freqLabels = [];
    for (const { freq, y } of painter.value.getFrequencyAxisTicks()) {
      freqLabels.push({
        top: `${y-5}px`,
        text: freq >= 1000 ? (freq / 1000) + 'k' : freq,
      })
    }
    yAxisPoints.value = freqLabels
  }


  //Playback

  const playbackIters = computed({
    get(){return _playbackIters.value},
    set(value){
      painter.value.setGlItersPlay(value);
    }
  })

  //Export
  const glItersExport = computed({
    get(){return _glItersExport.value},
    set(value){
      painter.value.setGlItersExport(value);
    }
  })

  //Other
  const name = computed({
    get(){return _name.value},
    set(value){
      painter.value.setName(value);
    }
  })

  //Painting
  const tool = computed({
    get(){return _tool.value},
    set(value){
      painter.value.setTool(value);
    }
  })

  const brushOpacity = computed({
    get(){return _brushOpacity.value},
    set(value){
      painter.value.setBrushOpacity(value / 100);
    }
  })

  const brushSize = computed({
    get(){return _brushSize.value},
    set(value){
      painter.value.setBrushSize(value);
    }
  })

  const brushSoftness = computed({
    get(){return _brushSoftness.value},
    set(value){
      painter.value.setBrushSoftness(value);
    }
  })

  const noiseScale = computed({
    get(){return _noiseScale.value},
    set(value){
      painter.value.setNoiseScale(value);
    }
  })

  // brushType/eraseMode have no direct setter on the painter, just a toggle,
  // so only actually toggle when the desired value differs from the current one.
  const brushType = computed({
    get(){return _brushType.value},
    set(value){
      if (value !== _brushType.value) painter.value.toggleNoiseBrush();
    }
  })

  const eraseMode = computed({
    get(){return _eraseMode.value},
    set(value){
      if (value !== _eraseMode.value) painter.value.toggleErase();
    }
  })

  const undoSteps = computed({
    get(){return _undoSteps.value},
    set(value){
      if (value !== _undoSteps.value) painter.value.setUndoLimit(value)
    }
  })


  const layerThumbnail = (layerCanvas) => {
    if (!layerCanvas){
      return ''
    }
    const tc = document.createElement('canvas');
    tc.width = tc.height = 64;
    const tctx = tc.getContext('2d');
    tctx.fillStyle = '#000';
    tctx.fillRect(0, 0, 64, 64);
    tctx.drawImage(layerCanvas, 0, 0, 64, 64);
    return tc.toDataURL();
  }


  const importCryFromPokemon = (pokemon, cry) => {
    let layerName = pokemon.name
    if (pokemon.gen <= 5){
      layerName += ` (${cry})`
    }
    painter.value.importAudioUrl(`/poketools/cries/${cry}/${pokemon.cries[cry]}`, layerName);
  }


  const showingBrowserStorageModal = ref(false)
  const storageSessions = ref(null)
  const showBrowserLoadModal = ()=>{
    storageSessions.value = painter.value.listStorageSessions()
    showingBrowserStorageModal.value = true
  }
  const confirmDelete = ref(null)

  const nameInput = ref()
  const checkName = () => {
    if (!_name.value){
      // Add a red border aorund the name input...
      console.log('NOPE')
      nameInput.value.classList.add('!bg-tool-cry-creator-secondary')
      setTimeout(()=>{
        nameInput.value.classList.remove('!bg-tool-cry-creator-secondary')
      }, 1000)
      return false
    }
    return true
  }

  const markSavedToBrowser = ref(false)
  const saveToStorage = ()=>{
    painter.value.saveToStorage()

    markSavedToBrowser.value = true
    setTimeout(()=>{
      markSavedToBrowser.value = false
    }, 2000)
  }


  const selectAllLayer = (layerId) => {
    painter.value.selectLayer(layerId);
    setTimeout(()=>{
      painter.value.selectAll()
    }, 100);
  }

</script>
<template>
  <div class="container mt-8 mb-20">
    <ToolPageHeader tool="cry-creator">
      <template #info>
        <p class="my-2">Paint on the canvas to create a spectrogram, and then export it as an audio file.</p>
        <p class="my-2">Import images, Oggs or WAVs as layers, or import and edit existing cries, or just doodle and make Bug-type cries. Be careful at the top end of the canvas, it gets piercing.</p>
        <p class="my-2 font-bold text-tool-cry-creator">Keyboard shortcuts:</p>
        <div class="flex flex-col lg:grid grid-cols-2 gap-2 my-2">
          <ul class=" list-disc pl-6">
            <li><b class="text-tool-cry-creator">B</b> - Brush mode, and toggle between paint and noise brushes</li>
            <li><b class="text-tool-cry-creator">E</b> - Eraser mode, which works like the current brush; you can also hold the <b class="text-tool-cry-creator">Alt</b> key in brush mode</li>
            <li>Hold <b class="text-tool-cry-creator">Shift</b> to draw straight lines</li>
            <li>Plus, hover the brush settings for more.</li>
          </ul>
          <ul class=" list-disc pl-6">
            <li><b class="text-tool-cry-creator">M</b> - Select (marquee) mode.
              <ul class="list-disc pl-6">
                <li>Hold <b class="text-tool-cry-creator">Shift</b> to scale linearly or rotate in increments</li>
                <li>Hold <b class="text-tool-cry-creator">Alt</b> to drag deselect boxes</li>
                <li>Hold <b class="text-tool-cry-creator">Shift</b> and <b class="text-tool-cry-creator">Alt</b> while scaling to warp</li>
              </ul>
            </li>
            <li><b class="text-tool-cry-creator">Ctrl/Cmd Z, Y/Shift-Z</b> - Undo and redo</li>
            <li><b class="text-tool-cry-creator">Ctrl/Cmd A</b> - Select all pixels on the layer</li>
          </ul>
        </div>
      </template>
      <template #controls>
        <div class="flex flex-col gap-2">
          <InputGroup label-key="name" tool="cry-creator">
            <template #label>
              Name:
            </template>
            <template #input>
              <input
                type="text"
                ref="nameInput"
                v-model="name"
                placeholder="Untitled"
              />
            </template>
          </InputGroup>

          <div class="flex gap-4">
            <input
              ref="cryFile"
              type="file"
              class="hidden"
              accept=".cry"
              @change="(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                if (file) painter.load(file);
              }"
            />

            <HoverDropdown tool='cry-creator'>
              <template #hoverable>
                <Button
                  tool="cry-creator"
                  class="flex-grow"
                  :icon="MonitorUpIcon"
                >
                  Load Session...
                </Button>
              </template>
              <div @click="()=>{ showBrowserLoadModal() }">Load from Browser</div>
              <div @click="()=>{cryFile.click()}">Load from Computer</div>
            </HoverDropdown>
            <HoverDropdown tool='cry-creator'>
              <template #hoverable>
                <Button
                  tool="cry-creator"
                  class="flex-grow"
                  :icon="MonitorDownIcon"
                >
                  Save Session...
                </Button>
              </template>
              <div @click="()=>{ if (!checkName()) { return }
               saveToStorage()
              }">Save to Browser <CheckIcon v-if="markSavedToBrowser" class="inline-block text-green-500"/> </div>
              <div @click="()=>{ if (checkName()) painter.save() }">Save to Computer</div>
            </HoverDropdown>
          </div>

          <template v-if="false">
            // whack the exprt file into a data: link or something idk
            <Button
              tool="cry-creator"
              :icon="ShareIcon"
            >Quick Share</Button>
          </template>
        </div>
      </template>
    </ToolPageHeader>


    <div class="my-12 flex flex-col lg:grid lg:grid-cols-3 gap-8">

      <BaseCard tool="cry-creator" class="md:col-span-2 flex flex-col gap-8">

        <div class="flex gap-12 flex-col lg:flex-row ">

          <div class="grow flex flex-col gap-2">
            <div class="flex flex-col lg:flex-row gap-2">
              <div>
                <Button
                  tool="cry-creator"
                  class="!rounded-r-none"
                  :secondary="!(tool === 'brush' && brushType === 'paint')"
                  :class="(tool === 'brush' && brushType === 'paint') ?'outline outline-tool-cry-creator z-10 relative' : ''"
                  :icon="BrushIcon"
                  @click="()=>{tool = 'brush'; brushType = 'paint'}"
                >Brush</Button>
                <Button
                  tool="cry-creator"
                  class="!rounded-l-none"
                  :secondary="!(tool === 'brush' && brushType === 'noise')"
                  :class="(tool === 'brush' && brushType === 'noise') ?'outline outline-tool-cry-creator': ''"
                  :icon="DicesIcon"
                  @click="()=>{tool = 'brush'; brushType = 'noise'}"
                >Noise</Button>


                <Button
                  tool="cry-creator"
                  :secondary="!eraseMode"
                  :class="eraseMode ?'outline outline-tool-cry-creator': ''"
                  :icon="EraserIcon"
                  @click="()=>{eraseMode = !eraseMode}"
                />
              </div>

              <div>
                <Button
                  tool="cry-creator"
                  :secondary="tool !== 'select'"
                  :class="tool === 'select' ?'outline outline-tool-cry-creator': ''"
                  :icon="SquareDashedIcon"
                  @click="()=>{tool = 'select'}"
                >Select</Button>
              </div>

            </div>
            <div class="grid lg:grid-cols-2 gap-2" v-auto-animate>

                <Tooltip text="Use the [ and ] keys to increment">
                  <InputGroup label-key="ratio" tool="cry-creator" small>
                    <template #label>
                      Size:
                    </template>
                    <template #input>
                      <input class="mx-2 w-24" type="range" v-model.number="brushSize" min="1" max="100"/>
                    </template>
                    <template #after>
                      {{ brushSize }}px
                    </template>
                  </InputGroup>
                </Tooltip>

                <Tooltip text="Use the 1-9 and 0 keys to set">
                  <InputGroup label-key="ratio" tool="cry-creator" small>
                    <template #label>
                      Opacity:
                    </template>
                    <template #input>
                      <input class="mx-2 w-24" type="range" v-model.number="brushOpacity" min="0" max="100"/>
                    </template>
                    <template #after>
                      {{ brushOpacity }}%
                    </template>
                  </InputGroup>
                </Tooltip>

                <InputGroup label-key="ratio" tool="cry-creator" small>
                  <template #label>
                    Softness:
                  </template>
                  <template #input>
                    <input class="mx-2 w-24" type="range" v-model.number="brushSoftness" min="0" max="10"/>
                  </template>
                  <template #after>
                    {{ brushSoftness }}
                  </template>
                </InputGroup>

                <InputGroup v-if="tool === 'brush' && brushType === 'noise'" label-key="ratio" tool="cry-creator" small>
                  <template #label>
                    Noise scale:
                  </template>
                  <template #input>
                    <input class="mx-2 w-24" type="range" v-model.number="noiseScale" min="1" max="100"/>
                  </template>
                  <template #after>
                    {{ noiseScale }}
                  </template>
                </InputGroup>

            </div>
          </div>

          <div class="flex flex-col gap-2 items-end">
            <div class="flex gap-2">
              <Button
                tool="cry-creator"
                secondary
                :icon="SquareIcon"
                :disabled="!isPlaying"
                @click="()=>{if (isPlaying) painter.stop()}"
              >Stop</Button>
              <Button
                tool="cry-creator"
                tertiary
                class="!text-black"
                :icon="PlayIcon"
                :disabled="isBusy"
                @click="()=>{if (!isBusy) painter.play()}"
              >Play</Button>
            </div>
            <div class="flex gap-2">

               <InputGroup label-key="ratio" tool="cry-creator" small>
                  <template #label>
                    Fast
                  </template>
                  <template #input>
                    <input class="mx-2 w-24" type="range" v-model.number="playbackIters" min="5" max="100"/>
                  </template>
                  <template #after>
                    Good
                  </template>
                </InputGroup>

            </div>
          </div>

        </div>






        <div class="relative mx-auto">
          <div class="freq-labels" id="freqLabels"></div>
          <div class="canvas-wrap relative">
            <div class="absolute w-8 top-0 -left-8">
              <div
                v-for="freqLabel in yAxisPoints"
                :key="freqLabel.top"
                class="text-xs font-mono text-right absolute right-0 pr-2"
                :style="{top: freqLabel.top}"
              >{{ freqLabel.text }}</div>
            </div>
            <canvas
              ref="mainCanvas"
              id="main"
              width="512"
              height="512"
              class="block border border-tool-cry-creator"
              :style="{
                cursor: 'crosshair',
                imageRendering: 'pixelated'
              }"
            ></canvas>
            <canvas
              ref="selectionOverlay"
              id="selOverlay"
              width="512"
              height="512"
              class="absolute top-0 left-0 pointer-events-none"
            ></canvas>
            <div
              ref="playhead"
              class="absolute top-0 pointer-events-none bg-tool-cry-creator/70"
              :style="{height:'512px', display:'none', width: '2px'}"
            ></div>
          </div>
          <div class="mt-2 h-8 relative bg-tool-cry-creator/10 rounded">
            <div class="rounded h-full bg-tool-cry-creator/50 transition-all" :style="{width: `${progress}%`}"></div>
            <div class="absolute text-sm top-0 bottom-0 left-0 right-0 flex items-center justify-around">
              <div>{{ statusText }}</div>
            </div>
          </div>
        </div>


      </BaseCard>


      <div class="flex flex-col md:grid md:grid-cols-2 lg:flex lg:flex-col gap-8">
        <BaseCard tool="cry-creator" class="flex flex-col gap-8">

          <div class="flex gap-2 justify-between">
            <h3 class="font-display">Layers</h3>

            <input
              ref="wavFile"
              type="file"
              accept=".wav,.ogg,.oga,audio/*"
              class="hidden"
              @change="(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                if (file) painter.importAudioFile(file);
              }"
            />
            <input
              ref="pngFile"
              type="file"
              accept="image/png,image/*"
              class="hidden"
              @change="(e) => {
                const file = e.target.files[0];
                e.target.value = '';
                if (file) painter.importPngFile(file);
              }"
            />

            <div class="flex gap-1">
              <Tooltip
                text="Import a Pokémon cry"
              >
                <PokemonSearch @select="importCryFromPokemon">
                  <template v-slot:button="{showModal}">
                    <Button
                      tool="cry-creator"
                      secondary
                      small
                      @click="()=>{showModal()}"
                    >
                      <template #icon>
                        <PokeballIcon/>
                      </template>
                      <PlusIcon class="w-4 h-4"/>
                    </Button>
                  </template>
                  <template #controls="controlsProps">
                    <Button
                      v-if="controlsProps.pokemon.gen <= 5"
                      tool="cry-creator"
                      small
                      secondary
                      @click="controlsProps.select(controlsProps.pokemon, 'classic')"
                    >
                      Classic Cry
                    </Button>
                    <Button
                      tool="cry-creator"
                      small
                      @click="controlsProps.select(controlsProps.pokemon, 'modern')"
                    >
                      {{ controlsProps.pokemon.gen <= 5 ? 'Modern Cry' : 'Import Cry' }}
                    </Button>
                  </template>
                </PokemonSearch>
              </Tooltip>

              <Tooltip
                text="Import .wav or .ogg"
              >
                <Button
                  tool="cry-creator"
                  secondary
                  small
                  :icon="AudioLinesIcon"
                  @click="()=>{wavFile.click()}"
                ><PlusIcon class="w-4 h-4"/></Button>
              </Tooltip>

              <Tooltip
                text="Import .png"
              >
                <Button
                  tool="cry-creator"
                  secondary
                  small
                  :icon="ImageIcon"
                  @click="()=>{pngFile.click()}"
                ><PlusIcon class="w-4 h-4"/></Button>
              </Tooltip>

              <Tooltip
                text="New blank layer"
              >
                <Button
                  tool="cry-creator"
                  small
                  :icon="PlusIcon"
                  @click="()=>{painter.addLayer()}"
                />
              </Tooltip>

            </div>
          </div>

          <div class="flex flex-col gap-2">

            <div
              v-for="layer, index in layers"
              :key="layer.id"
              class="flex gap-2 p-2"
              :class="selectedLayer == layer.id ? 'bg-tool-cry-creator/40' : 'bg-tool-cry-creator/20'"
              @click="() => {painter.selectLayer(layer.id)}"
            >
              <img
                class="w-16 h-16"
                :src="layerThumbnail(layer.canvas)"
                :class="layer.visible ? '' : 'opacity-50'"
              />
              <div class="flex-grow flex flex-col gap-1">
                <input
                  type="text"
                  class="small focus:outline !outline-tool-cry-creator !w-auto"
                  :value="layer.name"
                  @input="(e) => {
                    painter.renameLayer(layer.id, e.target.value);
                  }"
                />
                <div class="flex gap-1 justify-end">
                  <Button
                    small
                    tool='cry-creator'
                    :secondary="!layer.visible"
                    :icon="layer.visible ? EyeIcon : EyeClosedIcon"
                    @click="()=>{ painter.toggleLayerVisibility(layer.id) }"
                  />
                  <Button
                    small
                    tool='cry-creator'
                    :icon="SquareDashedIcon"
                    @click="()=>{ selectAllLayer(layer.id) }"
                  />
                  <Button
                    small
                    tool='cry-creator'
                    :icon="ArrowBigUpIcon"
                    :disabled="index==0"
                    @click="()=>{if(index!==0) painter.moveLayer(layer.id, index-1)}"
                  />
                  <Button
                    small
                    tool='cry-creator'
                    :icon="ArrowBigDownIcon"
                    :disabled="index==layers.length-1"
                    @click="()=>{if(!index==layers.length-1) painter.moveLayer(layer.id, index+1)}"
                  />
                  <Button
                    small
                    tool='cry-creator'
                    secondary
                    :icon="XIcon"
                    :disabled="layers.length == 1"
                    @click="()=>{if(layers.length != 1) painter.removeLayer(layer.id)}"
                  />

                </div>
              </div>
            </div>

          </div>
        </BaseCard>

        <BaseCard tool="cry-creator" class="flex flex-col gap-8">
          <h3 class="font-display">History</h3>

          <div class="flex gap-4 justify-around">
            <Button
              tool="cry-creator"
              :icon="UndoIcon"
              @click="()=>{ painter.undo() }"
            >
              Undo
            </Button>
            <Button
              tool="cry-creator"
              :icon="RedoIcon"
              @click="()=>{ painter.redo() }"
            >
              Redo
            </Button>
          </div>
          <InputGroup label-key="ratio" tool="cry-creator" small>
            <template #label>
              History Steps
            </template>
            <template #input>
              <input class="mx-2 w-24" type="range" v-model.number="undoSteps" min="1" max="100"/>
            </template>
            <template #after>
              {{ undoSteps }}
            </template>
          </InputGroup>

        </BaseCard>

        <BaseCard tool="cry-creator" class="flex flex-col gap-8">
          <h3 class="font-display">Export Audio</h3>
          <InputGroup label-key="ratio" tool="cry-creator" small>
            <template #label>
              Fast
            </template>
            <template #input>
              <input class="mx-2 w-24" type="range" v-model.number="glItersExport" min="5" max="200"/>
            </template>
            <template #after>
              Good
            </template>
          </InputGroup>

          <div class="flex gap-4 justify-around">
            <Button
              tool="cry-creator"
              :icon="DownloadIcon"
              :disabled="isBusy"
              @click="()=>{ if (!isBusy) painter.exportWav() }"
            >
              WAV
            </Button>
            <Button
              tool="cry-creator"
              :icon="DownloadIcon"
              :disabled="isBusy"
              @click="()=>{ if (!isBusy) painter.exportOgg() }"
            >
              OGG
            </Button>
          </div>
        </BaseCard>
      </div>
  </div>


  <Modal
    v-if="showingBrowserStorageModal"
    @close="()=>{showingBrowserStorageModal = false}"
  >
    <h3 class="font-display text-tool-cry-creator font-xl">Load Browser Session</h3>

    <div class="max-h-96 overflow-y-auto">
      <div class="flex flex-col gap-4">
        <div
          v-for="session in storageSessions"
          :key="session.id"
          class="bg-tool-cry-creator/30 hover:bg-tool-cry-creator/40 cursor-pointer p-4 flex gap-2"
        >
          <div class=" flex-grow flex flex-col gap-2">
            <div class="font-bold">{{ session.name }}</div>
            <div>{{  new Date(session.savedAt).toLocaleString() }}</div>
          </div>
          <div class="flex gap-2 justify-end items-center" v-auto-animate>

            <Button
              tool="cry-creator"
              secondary
              small
              @click="()=>{
                confirmDelete = session;
              }"
              :icon="XIcon"
            />
            <Button
              tool="cry-creator"
              small
              @click="()=>{
                painter.downloadFromStorage(session.id);
              }"
              :icon="DownloadIcon"
            />

            <Button
              tool="cry-creator"
              @click="async ()=>{
                await painter.loadFromStorage(session.id);
                showingBrowserStorageModal = false
              }"
            >Load</Button>
          </div>
        </div>

        <div v-if="painter.listStorageSessions().length == 0" class="text-center italic">
          No saved sessions.
        </div>
      </div>
    </div>
  </Modal>

  <Modal
    v-if="confirmDelete"
    @close="()=>{confirmDelete = null}"
  >
    <div>Really delete the session {{ confirmDelete.name }}?</div>
    <div class="flex justify-end gap-2">
      <Button
        tool="cry-creator"
        @click="()=>{
          confirmDelete =null;
        }"
      >Cancel</Button>
      <Button
        tool="cry-creator"
        secondary
        @click="()=>{
          painter.deleteFromStorage(confirmDelete.id);
          storageSessions = painter.listStorageSessions()
          confirmDelete =null;
        }"
      >Yes, delete it</Button>
    </div>
  </Modal>

</div>

</template>
